import React, { useEffect, useState, useRef } from "react";
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider,
  Controls,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import KnowledgeGraphNode, { findNodeColor } from "./KnowledgeGraphNode";
import { MetricEdgeType } from "build-ts/enums";
import { Header, MetricProto } from "build-ts/metadata";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
} from "d3-force";
import MKGNodeMenuContainer from "../../../containers/configuration/MKGNodeMenuContainer";
import { useConfigurationContext } from "context/ConfigurationContext";

interface KnowledgeGraphVisualizerProps {
  metricInfoMap: Map<string, MetricProto>;
}

const nodeTypes = {
  customMKGNode: KnowledgeGraphNode,
};

// Internal component that uses React Flow hooks
const KnowledgeGraphVisualizerFlow: React.FC<KnowledgeGraphVisualizerProps> = ({
  metricInfoMap,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [originalEdges, setOriginalEdges] = useState<any[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [layoutReady, setLayoutReady] = useState(false);
  const {selectedMetric, setSelectedMetric, selectedBenchmark, setSelectedBenchmark, selectedKgColumn, setSelectedKgColumn} = useConfigurationContext();
  const [metricNodesData, setMetricNodesData] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 1200, height: 800 });
 
  // Effect to handle from sidebar selection
  useEffect(()=>{
    if(selectedMetric || selectedBenchmark || selectedKgColumn) {
        setSelectedNodeId(selectedMetric?.id || selectedBenchmark?.id || selectedKgColumn?.id || null);
        
        // Apply edge highlighting when node is selected via props
        const nodeData = selectedMetric || selectedBenchmark || selectedKgColumn;
        const nodeId = selectedMetric?.id || selectedBenchmark?.id || selectedKgColumn?.id;
        if (nodeId) {
          const connectedEdges = originalEdges.filter(edge => 
            edge.source === nodeId || edge.target === nodeId
          );

          // update edges: highlight connected ones, dim others
          const updatedEdges = originalEdges.map(edge => {
            const isConnected = connectedEdges.some(connectedEdge => connectedEdge.id === edge.id);
            
            // Determine the original color based on edge type
            let originalColor = "#8133b2"; // default for metric edges
            if (edge.source && edge.target) {
              // Check if this is a metric-attribute edge by looking at the target node
              const targetNode = metricNodesData.find((node: any) => node.id === edge.target);
              if (targetNode && targetNode.data?.header?.type !== undefined) {
                // This is an attribute edge, use the color from findNodeColor
                const color = findNodeColor(targetNode.data.header);
                if (color) {
                  originalColor = color;
                }
              }
            }
            
            // TO CHECK - WHAT does this affect?
            //determines wether the edge is connected to the selected node, reduces opacity of unconnected edges
            return {
              ...edge,
              style: {
                ...edge.style,
                stroke: isConnected ? originalColor : "rgba(200,200,200,0.3)",
                strokeWidth: isConnected ? 4 : 4,
              },
              labelStyle: isConnected ? edge.labelStyle : {
                fill: "rgba(200,200,200,0.3)", 
                fontWeight: 500,
              },
              //adds an arrow head to the edge if it is connected to the selected node
              markerEnd: isConnected ? {
                type: MarkerType.ArrowClosed,
                color: originalColor,
                width: 10,
                height: 10,
                strokeWidth: 4,
              } : undefined
            };
          });
          setEdges(updatedEdges as any);

          // Dim unconnected nodes
          const connectedNodeIds = new Set([
            nodeId,
            ...originalEdges
              .filter(edge => edge.source === nodeId || edge.target === nodeId)
              .map(edge => (edge.source === nodeId ? edge.target : edge.source))
          ]);
          const updatedNodes = metricNodesData.map(n => ({
            ...n,
            style: {
              ...n.style,
              opacity: connectedNodeIds.has(n.id) ? 1 : 0.3,
            }
          }));
          setNodes(updatedNodes as any);
        }
        setShowMenu(true);
        setSelectedMetricForMenu(nodeData);
    }
    else {
        setSelectedNodeId(null);
        // Hide the graph menu when no node is selected
        setShowMenu(false);
        setSelectedMetricForMenu(null);
        // Reset edges when no node is selected
        setEdges(originalEdges.map(edge => ({
          ...edge,
          markerEnd: undefined,
        })) as any);
        // Reset all node opacities to 1
        setNodes(metricNodesData.map(n => ({
          ...n,
          style: {
            ...n.style,
            opacity: 1,
          }
        })) as any);
    }
  }, [selectedMetric, selectedBenchmark, selectedKgColumn]);

  // Effect to update container size, gets width and height of the container to centeralize the initial graph view
  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerSize({ width, height });
    }
  }, []);

  // Effect to update the graph view when the metricInfoMap changes
  useEffect(() => {
    if (metricInfoMap && metricInfoMap.size > 0 && containerSize.width && containerSize.height) {
      const metricNodesData: any[] = [];
      const metricEdgesData: any[] = [];
      const attributeMap: Map<string, string> = new Map();
      
      const addUniqueNode = (node: any) => {
        if (!metricNodesData.find(existingNode => existingNode.id === node.id)) {
          metricNodesData.push(node);
        }
      };
      metricInfoMap.forEach((metric, metricId) => {
        const metricNode = {
          id: metricId,
          type: "customMKGNode",
          data: {
            header: metric?.header,
          },
          position: {x: 0, y: 0},
        };

        addUniqueNode(metricNode);
      });

      metricInfoMap.forEach((metric, metricId) => {
        metric?.edges?.forEach((edge, index) => {
          switch (edge.edge_type) {
            case MetricEdgeType.METRIC_METRIC_EDGE:
              if (edge?.header?.id) {
                const relatedMetricId = edge.header.id;
                
                // Check if the target metric node already exists
                const targetMetricExists = metricNodesData.some(node => node.id === relatedMetricId);
                
                // If the metric doesn't exist yet, create a placeholder node
                if (!targetMetricExists) {
                  const metricNode = {
                    id: relatedMetricId,
                    type: "customMKGNode",
                    data: {
                        header: edge.header,
                    },
                    position: {x: 0, y: 0},
                  };
                  addUniqueNode(metricNode);
                }
                
                // Create edge between the two metric nodes
                const metricEdge = {
                  id: `${metricId}-${relatedMetricId}`,
                  source: metricId,
                  target: relatedMetricId,
                  animated: false,
                  style: {
                    stroke: "var(--chakra-colors-gray-300)",
                    strokeWidth: 4,
                  },
                  markerEnd: undefined,
                  type: undefined,
                };
                metricEdgesData.push(metricEdge);
              }
              break;
            case MetricEdgeType.METRIC_ATTRIBUTE_EDGE:
            case MetricEdgeType.METRIC_BENCHMARK_EDGE:
            case MetricEdgeType.METRIC_MEASURE_EDGE:
            case MetricEdgeType.METRIC_TIMEFRAME_EDGE:
              if (edge?.header?.name) {
                const attributeName = edge?.header?.name;
                let attributeNodeId = attributeMap.get(attributeName);

                if (!attributeNodeId) {
                  attributeNodeId = edge?.header?.id || "";
                  attributeMap.set(attributeName, attributeNodeId);

                  const attributeNode = {
                    id: attributeNodeId,
                    type: "customMKGNode",
                    data: {
                        header: edge.header,
                    },
                    position: {x: 0, y: 0},
                  };
                  addUniqueNode(attributeNode);
                }

                //creates the edge between the metric and the attribute node
                const attributeEdge = {
                  id: `${metricId}-${attributeNodeId}`,
                  source: metricId,
                  target: attributeNodeId,
                  animated: false,
                  style: {
                    stroke: "var(--chakra-colors-gray-300)",
                    strokeWidth: 4,
                  },
                  markerEnd: undefined,
                  type: undefined,
                };
                metricEdgesData.push(attributeEdge);
              }
              break;
            default:
              break;
          }
        });
      });

      if(metricNodesData.length === 0) {
        return;
      }

    //force directed layout      
    const nodes = metricNodesData;
      const links = metricEdgesData.map(e => ({
        source: e.source,
        target: e.target,
        strength: e.data?.weight || 1,
      }));
      // Initialize positions in a circle for better convergence
      nodes.forEach((node, i) => {
        node.x = Math.cos((2 * Math.PI * i) / nodes.length) * 500;
        node.y = Math.sin((2 * Math.PI * i) / nodes.length) * 500;
        // Increase font size for node labels
        if (!node.data) node.data = {};
        if (!node.data.style) node.data.style = {};
        node.data.style.fontSize = "28px";
      });
      // Run force simulation with shorter edges
      const sim = forceSimulation(nodes)
        .force("link", forceLink(links).id((d: any) => d.id).strength((d: any) => d.strength).distance(84))
        .force("charge", forceManyBody().strength(-125))
        .force("collide", forceCollide().radius(130))
        .force("center", forceCenter(containerSize.width / 2, containerSize.height / 2).strength(0.1))
        .alphaDecay(0.05)
        .stop();
      for (let i = 0; i < 300; i++) sim.tick();
      // Assign final positions
      nodes.forEach(node => {
        node.position = { x: node.x, y: node.y };
      });

      setNodes(metricNodesData as any);
      setEdges(metricEdgesData as any);
      setOriginalEdges(metricEdgesData as any);
      setMetricNodesData(metricNodesData);
      setLayoutReady(true);
    }
  }, [metricInfoMap, containerSize]);

  useEffect(() => {
      if (!layoutReady) return;
      
      // React Flow will handle the hierarchical layout automatically
      // No need for manual D3 force simulation
      setLayoutReady(false);
  }, [layoutReady]);

  // State for menu management
  const [showMenu, setShowMenu] = useState(false);
  const [selectedMetricForMenu, setSelectedMetricForMenu] = useState<Header | null>(null);

  //onNodeclick is used to display the menu when a node is clicked
  const onNodeClick = (event: React.MouseEvent, node: any) => {

    if(selectedNodeId===node.id && showMenu){
      setShowMenu(false);
      setSelectedMetricForMenu(null);
      return;
    }
    
    // Update selection state
    setSelectedNodeId(node.id);

    // Apply edge highlighting for click-based selection
    const connectedEdges = originalEdges.filter(edge => 
      edge.source === node.id || edge.target === node.id
    );

    // update edges: highlight connected ones, dim others
    const updatedEdges = originalEdges.map(edge => {
        const isConnected = connectedEdges.some(connectedEdge => connectedEdge.id === edge.id);
        
        // Determine the original color based on edge type
        let originalColor = "#8133b2"; // default for metric edges
        if (edge.source && edge.target) {
          // Check if this is a metric-attribute edge by looking at the target node
          const targetNode = metricNodesData.find((node: any) => node.id === edge.target);
          if (targetNode && targetNode.data?.header?.type !== undefined) {
            // This is an attribute edge, use the color from findNodeColor
            const color = findNodeColor(targetNode.data.header);
            if (color) {
              originalColor = color;
            }
          }
        }
        
        //updates the edge style based on wether it is connected to the selected node
        return {
            ...edge,
            style: {
               ...edge.style,
               stroke: isConnected ? originalColor : "var(--chakra-colors-gray-100)",
               strokeWidth: isConnected ? 4 : 0,
            },
            labelStyle: isConnected ? edge.labelStyle : {
             fill: "var(--chakra-colors-gray-300)", 
             fontWeight: 500,
            },
            //adds an arrow head to the edge if it is connected to the selected node
            markerEnd: isConnected ? {
                type: MarkerType.ArrowClosed,
                color: originalColor,
                width: 12,
                height: 12,
                strokeWidth: 3,
            } : undefined
        };
    });
    setEdges(updatedEdges as any);

    // Dim unconnected nodes
    const connectedNodeIds = new Set([
      node.id,
      ...originalEdges
        .filter(edge => edge.source === node.id || edge.target === node.id)
        .map(edge => (edge.source === node.id ? edge.target : edge.source))
    ]);
    const updatedNodes = metricNodesData.map(n => ({
      ...n,
      style: {
        ...n.style,
        opacity: connectedNodeIds.has(n.id) ? 1 : 0.3,
      }
    }));
    setNodes(updatedNodes as any);

    // Handle menu display logic
    const nodeData = node.data.header as Header; 
    if(nodeData){
      setSelectedMetricForMenu(nodeData);
      setShowMenu(true);
      
    }
  };

  //resets all the nodes and edges when the pane is clicked
  const onPaneClick = (event: React.MouseEvent) => {
    setSelectedNodeId(null);
    setShowMenu(false);
    setSelectedMetricForMenu(null);
    setSelectedBenchmark(null as any);
    setSelectedKgColumn(null as any);
    setSelectedMetric(null as any);
    // Reset all edges to original colors and remove arrow heads
    setEdges(originalEdges.map(edge => ({
      ...edge,
      markerEnd: undefined,
    })) as any);
    // Reset all node opacities to 1
    setNodes(metricNodesData.map(n => ({
      ...n,
      style: {
        ...n.style,
        opacity: 1,
      }
    })) as any);
  };

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100vh" }}>
      <ReactFlow 
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        defaultViewport={{x:0, y:0, zoom:1}}
        fitView={true}
        fitViewOptions={{ padding: 50, maxZoom: 1.5, minZoom: 0.5 }}
        proOptions={{hideAttribution: true}}
        minZoom={0.1}
      >
        <Background />
        <Controls position="top-right" style={{ marginTop: "50px", transform: "scale(1.4)"}} />
      </ReactFlow>
      
      {/* Render menu when a node is clicked */}
      {showMenu && selectedMetricForMenu && (
        <MKGNodeMenuContainer  
          isClicked={showMenu}
          selectedMetricHeader={selectedMetricForMenu}
        />
      )}
    </div>
  );
};

// Main component with ReactFlowProvider wrapper
const KnowledgeGraphVisualizer: React.FC<KnowledgeGraphVisualizerProps> = ({
  metricInfoMap,
}) => {
  return (
    <ReactFlowProvider>
      <KnowledgeGraphVisualizerFlow metricInfoMap={metricInfoMap} />
    </ReactFlowProvider>
  );
};

export default KnowledgeGraphVisualizer;